import { Link } from 'react-router';
import { Check, Clock, Pencil, Play, type LucideIcon } from 'lucide-react';
import { type ChangeManagement } from '@/hooks/useChangeManagements';
import { cn } from '@/lib/utils';

interface CMDashboardProps {
  cms: ChangeManagement[];
  userId: string;
}

interface Bucket {
  key: string;
  label: string;
  icon: LucideIcon;
  accent: string;
  items: ChangeManagement[];
}

/**
 * "Needs your attention" summary above the change list: buckets the user's changes
 * into the actionable groups (awaiting your approval, in progress, approved, your
 * drafts). Renders nothing when there's nothing actionable.
 */
export default function CMDashboard({ cms, userId }: CMDashboardProps) {
  const inProgressStates = ['in_progress', 'paused', 'verification'];

  const buckets: Bucket[] = [
    {
      key: 'approve',
      label: 'Awaiting your approval',
      icon: Check,
      accent: 'text-sev-3',
      items: cms.filter((c) => c.needsMyApproval),
    },
    {
      key: 'progress',
      label: 'In progress',
      icon: Play,
      accent: 'text-brand',
      items: cms.filter((c) => inProgressStates.includes(c.state)),
    },
    {
      key: 'scheduled',
      label: 'Approved / scheduled',
      icon: Clock,
      accent: 'text-sev-5',
      items: cms.filter((c) => c.state === 'approved'),
    },
    {
      key: 'drafts',
      label: 'Your drafts',
      icon: Pencil,
      accent: 'text-muted-foreground',
      items: cms.filter((c) => c.state === 'draft' && c.createdBy === userId),
    },
  ].filter((b) => b.items.length > 0);

  if (buckets.length === 0) return null;

  return (
    <section className="mb-5">
      <h2 className="text-sm font-semibold mb-3">Needs your attention</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {buckets.map((b) => {
          const Icon = b.icon;
          return (
            <div key={b.key} className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={cn('w-4 h-4', b.accent)} aria-hidden="true" />
                <span className="text-sm font-medium">{b.label}</span>
                <span className="ml-auto text-sm tabular-nums text-muted-foreground">
                  {b.items.length}
                </span>
              </div>
              <ul className="space-y-1">
                {b.items.slice(0, 5).map((cm) => (
                  <li key={cm.id} className="truncate">
                    <Link
                      to={`/change-management/${cm.id}`}
                      className="text-sm hover:underline"
                    >
                      {cm.title}
                    </Link>
                  </li>
                ))}
                {b.items.length > 5 ? (
                  <li className="text-xs text-muted-foreground">
                    +{b.items.length - 5} more
                  </li>
                ) : null}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}
